import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

export type EntityPerformanceSortBy =
  | 'strategicGoalsAchievement'
  | 'performanceDeviation'
  | 'entityName';

export type SortOrder = 'asc' | 'desc';

interface PersistedSort {
  sortBy: EntityPerformanceSortBy;
  order: SortOrder;
}

const STORAGE_KEY = 'sms_entity_performance_sort';
const DEFAULT_SORT: PersistedSort = {
  sortBy: 'performanceDeviation',
  order: 'desc',
};
const VALID_SORT_BYS: EntityPerformanceSortBy[] = [
  'strategicGoalsAchievement',
  'performanceDeviation',
  'entityName',
];

function loadPersistedSort(): PersistedSort {
  if (Platform.OS !== 'web') return DEFAULT_SORT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SORT;
    const parsed = JSON.parse(raw) as Partial<PersistedSort>;
    const sortBy =
      parsed.sortBy && VALID_SORT_BYS.includes(parsed.sortBy as EntityPerformanceSortBy)
        ? (parsed.sortBy as EntityPerformanceSortBy)
        : DEFAULT_SORT.sortBy;
    const order: SortOrder = parsed.order === 'asc' ? 'asc' : 'desc';
    return { sortBy, order };
  } catch {
    return DEFAULT_SORT;
  }
}

function persistSort(sort: PersistedSort): void {
  if (Platform.OS !== 'web') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // Ignore storage failures (private mode, quota exceeded).
  }
}

interface SortableOrg {
  id: number | string;
  name: string;
  commitment_index?: number | null;
  stage_actual_progress?: number | null;
}

interface UseEntityPerformanceSortResult<T> {
  sortBy: EntityPerformanceSortBy;
  order: SortOrder;
  setSortBy: (sortBy: EntityPerformanceSortBy) => void;
  toggleOrder: () => void;
  sortedItems: T[];
}

export function useEntityPerformanceSort<T extends SortableOrg>(
  items: T[],
  language: 'ar' | 'en',
  translateName?: (name: string) => string,
): UseEntityPerformanceSortResult<T> {
  const [sortBy, setSortByState] = useState<EntityPerformanceSortBy>(DEFAULT_SORT.sortBy);
  const [order, setOrder] = useState<SortOrder>(DEFAULT_SORT.order);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const persisted = loadPersistedSort();
    setSortByState(persisted.sortBy);
    setOrder(persisted.order);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    persistSort({ sortBy, order });
  }, [sortBy, order, hydrated]);

  const setSortBy = useCallback((next: EntityPerformanceSortBy) => {
    setSortByState(next);
  }, []);

  const toggleOrder = useCallback(() => {
    setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  const sortedItems = useMemo<T[]>(() => {
    if (items.length < 2) return items;
    const collator = new Intl.Collator(language === 'ar' ? 'ar' : 'en', {
      sensitivity: 'base',
      numeric: true,
    });
    const direction = order === 'asc' ? 1 : -1;

    const numericValue = (org: T): number => {
      if (sortBy === 'strategicGoalsAchievement') {
        return Number(org.stage_actual_progress ?? 0) * 100;
      }
      if (sortBy === 'performanceDeviation') {
        const commitment = org.commitment_index;
        if (commitment == null) return -Infinity;
        return Math.max(0, Math.min(100, 100 - Number(commitment)));
      }
      return 0;
    };

    return [...items].sort((a, b) => {
      if (sortBy === 'entityName') {
        const labelA = translateName ? translateName(a.name) : a.name;
        const labelB = translateName ? translateName(b.name) : b.name;
        return direction * collator.compare(labelA, labelB);
      }
      const valueA = numericValue(a);
      const valueB = numericValue(b);
      if (valueA === valueB) {
        const nameA = translateName ? translateName(a.name) : a.name;
        const nameB = translateName ? translateName(b.name) : b.name;
        return collator.compare(nameA, nameB);
      }
      return direction * (valueA - valueB);
    });
  }, [items, sortBy, order, language, translateName]);

  return { sortBy, order, setSortBy, toggleOrder, sortedItems };
}
